// PATH: src/pages/CurriculumOnboardingPage.tsx — onboarding « programme marocain ».
//
// Première connexion de la direction : l'établissement suit-il le programme
// marocain ? Oui → les niveaux (1AP, 1AC, 2BAC-PC…) et leurs matières sont
// importés du catalogue tc_embed — le même que celui de tc_chat_ui — et
// VERROUILLÉS. Non → configuration manuelle, comme avant.
//
// Rouvert plus tard (établissement déjà « MA ») pour AJOUTER des niveaux du
// programme : les niveaux déjà importés restent cochés et non décochables.
//
// Le catalogue est lu ici, depuis le navigateur, pour l'affichage ; l'import
// lui-même est refait côté serveur, qui relit le catalogue et ne fait jamais
// confiance aux libellés envoyés.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Card, Col, Empty, List, Result, Row, Space, Spin, Steps, Tag, Tree, Typography, message, theme,
} from "antd";
import { BankOutlined, BookOutlined, EditOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurriculumApi, type CurriculumMode } from "../api/curriculum";
import { useEmbedCatalogApi, type EmbedSubject } from "../api/embedCatalog";
import { useSchoolApi } from "../api/school";
import {
  allLeafKeys,
  buildTreeData,
  importedKeys,
  isLeafKey,
  leafLabel,
  parseSelectionKey,
  selectionKey,
  selectionsFromChecked,
  type CatalogStageNode,
} from "../lib/curriculumTree";

type Step = "choix" | "niveaux" | "confirmation";
type Choice = "MA" | "CUSTOM";

interface Catalog {
  stages: CatalogStageNode[];
  subjects: EmbedSubject[];
}

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "var(--app-bg)", padding: "32px 16px" }}>
    <div className="app-fade-in" style={{ maxWidth: 960, margin: "0 auto" }}>
      <div className="login-brand" style={{ marginBottom: 20 }}>
        <span className="login-mark">
          <BankOutlined style={{ color: "#93B4FF", fontSize: 19 }} />
        </span>
        <Typography.Text className="login-wordmark">TilmiSchool</Typography.Text>
      </div>
      {children}
    </div>
  </div>
);

const Chargement: React.FC<{ label?: string }> = ({ label }) => (
  <div style={{ display: "grid", placeItems: "center", gap: 12, padding: "64px 0" }}>
    <Spin size="large" />
    {label && <Typography.Text type="secondary">{label}</Typography.Text>}
  </div>
);

const CurriculumOnboardingPage: React.FC = () => {
  const isBusiness = useAuth().user?.userTypeCode === "BUSINESS";
  const navigate = useNavigate();
  const curriculum = useCurriculumApi();
  const embed = useEmbedCatalogApi();
  const school = useSchoolApi();
  const { token } = theme.useToken();

  const [mode, setMode] = useState<CurriculumMode | undefined>();
  const [modeError, setModeError] = useState(false);
  const [step, setStep] = useState<Step>("choix");
  const [choice, setChoice] = useState<Choice | null>(null);
  const [saving, setSaving] = useState(false);

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [catalogState, setCatalogState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [imported, setImported] = useState<Set<string>>(new Set());
  const [checked, setChecked] = useState<string[]>([]);
  const [focused, setFocused] = useState<string | null>(null);
  // Matières d'une feuille : undefined = pas encore lues, null = lecture impossible.
  const [leafSubjects, setLeafSubjects] = useState<Record<string, string[] | null>>({});

  /** Ajout de niveaux à un établissement déjà passé au programme marocain. */
  const isAjout = mode === "MA";

  const loadMode = useCallback(() => {
    setModeError(false);
    setMode(undefined);
    curriculum
      .get()
      .then((c) => {
        const m = c.mode ?? "";
        setMode(m);
        setStep(m === "MA" ? "niveaux" : "choix");
      })
      .catch(() => setModeError(true));
  }, [curriculum]);

  useEffect(() => {
    if (isBusiness) loadMode();
  }, [isBusiness, loadMode]);

  const loadCatalog = useCallback(async () => {
    setCatalogState("loading");
    try {
      const program = await embed.getMoroccanProgram();
      if (!program) throw new Error("programme marocain absent du catalogue");
      const [stageList, subjects, levels] = await Promise.all([
        embed.getStages(program.id),
        embed.getSubjects(program.id),
        school.listLevels(),
      ]);
      const stages: CatalogStageNode[] = await Promise.all(
        stageList.map(async (stage) => {
          const grades = await embed.getGrades(stage.id);
          return {
            stage,
            grades: await Promise.all(grades.map(async (grade) => ({ grade, tracks: await embed.getTracks(grade.id) }))),
          };
        })
      );
      const done = importedKeys(levels);
      setCatalog({ stages, subjects });
      setImported(done);
      // Premier passage : tout le programme est proposé. Ajout : seuls les
      // niveaux déjà importés sont cochés, l'école coche ce qu'elle ajoute.
      setChecked(isAjout ? Array.from(done) : allLeafKeys(stages));
      setCatalogState("ready");
    } catch {
      setCatalogState("error");
    }
  }, [embed, school, isAjout]);

  useEffect(() => {
    if (step === "niveaux" && catalogState === "idle") loadCatalog();
  }, [step, catalogState, loadCatalog]);

  // Matières de la feuille sélectionnée : lues à la demande, gardées en cache.
  useEffect(() => {
    if (!focused || !catalog || leafSubjects[focused] !== undefined) return;
    let cancelled = false;
    const { grade_id, track_id } = parseSelectionKey(focused);
    const names = new Map(catalog.subjects.map((s) => [s.id.toLowerCase(), s.name]));
    embed
      .getOfferings(grade_id, track_id)
      .then((offerings) => {
        const list = Array.from(
          new Set(offerings.map((o) => names.get(o.subjectId.toLowerCase())).filter((n): n is string => !!n))
        );
        if (!cancelled) setLeafSubjects((prev) => ({ ...prev, [focused]: list }));
      })
      .catch(() => {
        if (!cancelled) setLeafSubjects((prev) => ({ ...prev, [focused]: null }));
      });
    return () => { cancelled = true; };
  }, [focused, catalog, embed, leafSubjects]);

  const treeData = useMemo(() => (catalog ? buildTreeData(catalog.stages, imported) : []), [catalog, imported]);
  const selections = useMemo(() => selectionsFromChecked(checked, imported), [checked, imported]);

  // Récapitulatif groupé par cycle, dans l'ordre du catalogue.
  const groups = useMemo(() => {
    if (!catalog) return [];
    const chosen = new Set(selections.map((s) => selectionKey(s.grade_id, s.track_id)));
    return catalog.stages
      .map(({ stage, grades }) => ({
        stage: stage.name,
        labels: grades
          .flatMap(({ grade, tracks }) =>
            tracks.length === 0 ? [selectionKey(grade.id)] : tracks.map((t) => selectionKey(grade.id, t.id))
          )
          .filter((k) => chosen.has(k))
          .map((k) => leafLabel(catalog.stages, k)),
      }))
      .filter((g) => g.labels.length > 0);
  }, [catalog, selections]);

  const apiError = (err: any, fallback: string) => message.error(err?.response?.data?.error || fallback);

  const chooseCustom = () => {
    setSaving(true);
    curriculum
      .apply({ mode: "CUSTOM" })
      .then(() => {
        message.success("Configuration manuelle retenue");
        navigate("/", { replace: true });
      })
      .catch((err) => apiError(err, "Enregistrement impossible"))
      .finally(() => setSaving(false));
  };

  const runImport = () => {
    setSaving(true);
    curriculum
      .apply({ mode: "MA", selections })
      .then((r) => {
        const niveaux = r.levels_created + r.levels_adopted;
        const matieres = r.subjects_created + r.subjects_adopted;
        message.success(`${niveaux} niveau(x) et ${matieres} matière(s) importés du programme marocain`);
        if (r.warnings.length > 0) message.warning(r.warnings.join(" · "), 8);
        navigate("/referentiels?tab=levels", { replace: true });
      })
      .catch((err) => apiError(err, "Import impossible"))
      .finally(() => setSaving(false));
  };

  if (!isBusiness) {
    return (
      <Shell>
        <Result status="403" title="Réservé à la direction de l'établissement"
          extra={<Button onClick={() => navigate("/", { replace: true })}>Retour</Button>} />
      </Shell>
    );
  }

  if (modeError) {
    return (
      <Shell>
        <Result status="warning" title="Configuration illisible"
          subTitle="Impossible de lire la configuration de l'établissement."
          extra={<Button type="primary" onClick={loadMode}>Réessayer</Button>} />
      </Shell>
    );
  }

  if (mode === undefined) {
    return <Shell><Chargement /></Shell>;
  }

  if (mode === "CUSTOM") {
    return (
      <Shell>
        <Result status="info" title="Votre établissement est configuré manuellement"
          subTitle="Niveaux et matières se gèrent depuis les Référentiels et l'Admin pédagogique."
          extra={<Button type="primary" onClick={() => navigate("/", { replace: true })}>Retour</Button>} />
      </Shell>
    );
  }

  const stepKeys: Step[] = isAjout ? ["niveaux", "confirmation"] : ["choix", "niveaux", "confirmation"];
  const stepTitles: Record<Step, string> = { choix: "Programme", niveaux: "Niveaux", confirmation: "Confirmation" };

  const choiceCard = (value: Choice, icon: React.ReactNode, title: string, description: string) => {
    const selected = choice === value;
    return (
      <Card
        hoverable
        role="radio"
        aria-checked={selected}
        tabIndex={0}
        onClick={() => setChoice(value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setChoice(value); } }}
        style={{
          height: "100%",
          borderWidth: 2,
          borderColor: selected ? token.colorPrimary : undefined,
          background: selected ? token.colorPrimaryBg : undefined,
        }}
      >
        <Space align="start" size={14}>
          <span style={{ fontSize: 24, color: token.colorPrimary }}>{icon}</span>
          <div>
            <Typography.Text strong style={{ fontSize: 16 }}>{title}</Typography.Text>
            <Typography.Paragraph type="secondary" style={{ margin: "6px 0 0" }}>{description}</Typography.Paragraph>
          </div>
        </Space>
      </Card>
    );
  };

  const focusedSubjects = focused ? leafSubjects[focused] : undefined;

  return (
    <Shell>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {isAjout ? "Ajouter des niveaux du programme marocain" : "Bienvenue sur TilmiSchool"}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
        {isAjout
          ? "Les niveaux déjà importés restent verrouillés ; cochez ceux que vous ajoutez."
          : "Configurons les niveaux et les matières de votre établissement."}
      </Typography.Paragraph>

      <Steps
        size="small"
        current={stepKeys.indexOf(step)}
        items={stepKeys.map((k) => ({ title: stepTitles[k] }))}
        style={{ margin: "16px 0 24px" }}
      />

      {step === "choix" && (
        <>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Votre établissement suit-il le programme marocain ?
          </Typography.Title>
          <Row gutter={[16, 16]} role="radiogroup">
            <Col xs={24} md={12}>
              {choiceCard("MA", <BookOutlined />, "Oui, programme marocain",
                "Niveaux (1AP, 1AC, 1BAC…) et matières sont repris du programme officiel. Vous pourrez toujours ajouter vos propres niveaux et matières.")}
            </Col>
            <Col xs={24} md={12}>
              {choiceCard("CUSTOM", <EditOutlined />, "Non, je configure moi-même",
                "Vous saisissez vos niveaux et matières comme aujourd'hui.")}
            </Col>
          </Row>
          <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              size="large"
              disabled={!choice}
              loading={saving}
              onClick={() => (choice === "CUSTOM" ? chooseCustom() : setStep("niveaux"))}
            >
              Continuer
            </Button>
          </div>
        </>
      )}

      {step === "niveaux" && (
        <>
          {catalogState === "loading" || catalogState === "idle" ? (
            <Chargement label="Chargement du programme marocain…" />
          ) : catalogState === "error" ? (
            <Result
              status="warning"
              title="Catalogue indisponible"
              subTitle="Le programme marocain n'a pas pu être chargé."
              extra={[
                <Button key="retry" type="primary" onClick={loadCatalog}>Réessayer</Button>,
                !isAjout && (
                  <Button key="manual" loading={saving} onClick={chooseCustom}>Configurer manuellement</Button>
                ),
              ]}
            />
          ) : (
            <>
              <Alert
                type="info"
                showIcon
                icon={<LockOutlined />}
                style={{ marginBottom: 16 }}
                message="Les niveaux et matières importés sont verrouillés : ils ne pourront être ni modifiés ni supprimés. Vous pourrez toujours ajouter les vôtres."
              />
              <Row gutter={[16, 16]}>
                <Col xs={24} md={14}>
                  <Card title="Niveaux enseignés" size="small">
                    {treeData.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun niveau dans le catalogue" />
                    ) : (
                      <Tree
                        checkable
                        defaultExpandAll
                        treeData={treeData}
                        checkedKeys={checked}
                        onCheck={(keys) => setChecked((Array.isArray(keys) ? keys : keys.checked).map(String))}
                        selectedKeys={focused ? [focused] : []}
                        onSelect={(keys) => {
                          const key = String(keys[0] ?? "");
                          if (isLeafKey(key)) setFocused(key);
                        }}
                      />
                    )}
                  </Card>
                </Col>
                <Col xs={24} md={10}>
                  <Card
                    title={focused && catalog ? `Matières — ${leafLabel(catalog.stages, focused)}` : "Matières"}
                    size="small"
                  >
                    {!focused ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sélectionnez un niveau" />
                    ) : focusedSubjects === undefined ? (
                      <Spin />
                    ) : focusedSubjects === null ? (
                      <Typography.Text type="secondary">Matières indisponibles pour ce niveau.</Typography.Text>
                    ) : focusedSubjects.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune matière" />
                    ) : (
                      <Space size={[6, 8]} wrap>
                        {focusedSubjects.map((name) => <Tag key={name}>{name}</Tag>)}
                      </Space>
                    )}
                  </Card>
                </Col>
              </Row>
              <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                {isAjout ? (
                  <Button onClick={() => navigate("/referentiels?tab=levels")}>Annuler</Button>
                ) : (
                  <Button onClick={() => setStep("choix")}>Retour</Button>
                )}
                <Button type="primary" size="large" disabled={selections.length === 0} onClick={() => setStep("confirmation")}>
                  Continuer
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {step === "confirmation" && (
        <>
          <Card size="small" title="Récapitulatif">
            <Typography.Paragraph>
              <strong>{selections.length} niveau(x)</strong> seront ajoutés, avec leurs matières, et verrouillés
              (non modifiables).
            </Typography.Paragraph>
            <List
              size="small"
              dataSource={groups}
              renderItem={(g) => (
                <List.Item>
                  <Space direction="vertical" size={6} style={{ width: "100%" }}>
                    <Typography.Text strong>{g.stage}</Typography.Text>
                    <Space size={[6, 8]} wrap>
                      {g.labels.map((label) => <Tag key={label} icon={<LockOutlined />}>{label}</Tag>)}
                    </Space>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
          <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <Button onClick={() => setStep("niveaux")} disabled={saving}>Retour</Button>
            <Button type="primary" size="large" loading={saving} onClick={runImport}>
              Importer
            </Button>
          </div>
        </>
      )}
    </Shell>
  );
};

export default CurriculumOnboardingPage;
